import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_WITH_CREDENTIALS = process.env.REACT_APP_API_WITH_CREDENTIALS !== 'false';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: API_WITH_CREDENTIALS,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url.includes('/auth/')) {
      // Session expired — clear token and redirect to login WITHOUT clearing user data cache silently.
      // The router-level redirect ensures the user knows they were signed out; state is naturally rebuilt on re-login.
      localStorage.removeItem('access_token');
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login?expired=1');
      }
    }
    return Promise.reject(error);
  }
);

const DEFAULT_API_ERROR = 'Something went wrong. Please try again.';
const GENERIC_ERROR_LOCATIONS = new Set(['body', 'query', 'path', 'header', 'cookie']);

const formatValidationLocation = (location) => {
  if (!Array.isArray(location)) return '';

  const fields = location.filter(
    (part) => typeof part === 'string' && !GENERIC_ERROR_LOCATIONS.has(part.toLowerCase())
  );
  const field = fields[fields.length - 1];
  if (!field) return '';

  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatValidationEntry = (entry) => {
  if (typeof entry === 'string') return entry.trim();
  if (!entry || typeof entry !== 'object') return '';

  const message = typeof entry.msg === 'string'
    ? entry.msg.trim()
    : typeof entry.message === 'string'
      ? entry.message.trim()
      : '';
  if (!message) return '';

  const field = formatValidationLocation(entry.loc);
  return field ? `${field}: ${message}` : message;
};

export const formatApiErrorDetail = (detail, fallback = DEFAULT_API_ERROR) => {
  const entries = Array.isArray(detail) ? detail : [detail];
  const messages = entries.map(formatValidationEntry).filter(Boolean);
  return messages.length ? messages.join('; ') : fallback;
};

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (identifier) => api.post('/auth/forgot-password', { identifier }),
  resetPassword: (token, newPassword, confirmPassword) => api.post('/auth/reset-password', { token, new_password: newPassword, confirm_password: confirmPassword }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })
};

// Per-user preferences
export const preferencesAPI = {
  setTimeFormat: (timeFormat) => api.put('/users/me/time-format', { time_format: timeFormat })
};

// Activity / Audit log (Super Admin)
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  filters: () => api.get('/audit-logs/actions')
};

// Field-level visibility (Super Admin)
export const fieldVisibilityAPI = {
  list: () => api.get('/field-visibility'),
  set: (data) => api.put('/field-visibility', data)
};

// Batch 2A: dynamic lead sources management (Super Admin)
export const clientSectorAPI = {
  list: () => api.get('/client-sectors'),
  listAll: () => api.get('/client-sectors/all'),
  create: (data) => api.post('/client-sectors', data),
  update: (sectorId, data) => api.put(`/client-sectors/${sectorId}`, data),
};

export const leadSourceAdminAPI = {
  listAll: () => api.get('/lead-sources/all'),
  create: (data) => api.post('/lead-sources', data),
  update: (id, data) => api.put(`/lead-sources/${id}`, data),
  disable: (id) => api.delete(`/lead-sources/${id}`)
};

// Batch 2A: re-enquiry (B2C), work queue, assignment intelligence, lead follow-up
export const reEnquiryAPI = {
  existingByMobile: (mobile) => api.get('/leads/existing-by-mobile', { params: { mobile } }),
  create: (leadId, data) => api.post(`/leads/${leadId}/re-enquiry`, data),
  list: () => api.get('/leads/re-enquiries')
};
export const workQueueAPI = {
  get: (userId) => api.get('/sales/work-queue', { params: userId ? { user_id: userId } : {} })
};
export const assignmentAPI = {
  intelligence: () => api.get('/leads/assignment-intelligence')
};
export const leadFollowUpAPI = {
  add: (leadId, data) => api.post(`/leads/${leadId}/follow-up`, data)
};

// Batch 2B: projects, payments, serial, architect assignment, B2B
export const projectAPI = {
  list: (params) => api.get('/projects', { params }),
  setSerial: (leadId, serial) => api.put(`/leads/${leadId}/serial`, { project_serial: serial }),
  editSerial: (leadId, serial, reason) => api.put(`/leads/${leadId}/serial/edit`, { project_serial: serial, reason }),
  // B5B — the single canonical creation pipeline (Lead -> Project and Direct Project alike)
  create: (data) => api.post('/projects', data),
  forLead: (leadId) => api.get(`/leads/${leadId}/projects`),
  resolve: (projectId) => api.get(`/projects/${projectId}/resolve`)
};
export const clientAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  overview: (id) => api.get(`/clients/${id}/overview`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data)
};
export const paymentAPI = {
  summary: (params) => api.get('/payments/summary', { params }),
  reminders: () => api.get('/payments/reminders'),
  record: (leadId, data) => api.post(`/leads/${leadId}/payment`, data),
  confirm: (leadId, paymentId) => api.post(`/leads/${leadId}/payment/confirm`, null, { params: paymentId ? { payment_id: paymentId } : {} }),
  overrideGate: (leadId, reason) => api.post(`/leads/${leadId}/payment-gate/override`, { reason }),
  ledger: (leadId) => api.get(`/leads/${leadId}/payment-ledger`),
  reverse: (leadId, paymentId, reason) => api.post(`/leads/${leadId}/payment/${paymentId}/reverse`, { reason }),
  setContractValue: (leadId, amount, reason) => api.put(`/leads/${leadId}/contract-value`, { amount, reason })
};
export const architectAssignAPI = {
  intelligence: () => api.get('/architect/assignment-intelligence'),
  assign: (leadId, architectId, reason) => api.post(`/architect/assign/${leadId}`, null, { params: { architect_id: architectId, reason } })
};
export const b2bAPI = {
  list: () => api.get('/b2b-partners'),
  get: (id) => api.get(`/b2b-partners/${id}`),
  create: (data) => api.post('/b2b-partners', data),
  update: (id, data) => api.put(`/b2b-partners/${id}`, data)
};

// User APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { new_password: newPassword }),
  toggleActive: (userId) => api.post(`/users/${userId}/toggle-active`),
  updateMyNotifications: (enabled) => api.put(`/users/me/notifications?enabled=${enabled ? 'true' : 'false'}`)
};

// Global notification setting APIs
export const notificationSettingsAPI = {
  getGlobal: () => api.get('/settings/notifications'),
  setGlobal: (enabled) => api.put(`/settings/notifications?enabled=${enabled ? 'true' : 'false'}`)
};

// Role & Permissions APIs
export const rbacAPI = {
  getAllPermissions: () => api.get('/permissions'),
  getRoles: () => api.get('/roles'),
  updateRolePermissions: (role, permissions) => api.put(`/roles/${role}/permissions`, { permissions }),
  createRole: (data) => api.post('/roles', data),
  updateRole: (role, data) => api.put(`/roles/${role}`, data),
  archiveRole: (role) => api.delete(`/roles/${role}`)
};

// Global Location reference data (server-side search; the client never downloads the dataset)
export const locationAPI = {
  countries: (q) => api.get('/locations/countries', { params: q ? { q } : {} }),
  states: (country, q) => api.get('/locations/states', { params: { country, ...(q ? { q } : {}) } }),
  districts: (country, state, q) => api.get('/locations/districts', { params: { country, state, ...(q ? { q } : {}) } }),
  cities: (country, state, district, q) => api.get('/locations/cities', { params: { country, state, ...(district ? { district } : {}), ...(q ? { q } : {}) } }),
  localities: (city, country, q) => api.get('/locations/localities', { params: { city, ...(country ? { country } : {}), ...(q ? { q } : {}) } }),
};

// Lead APIs
export const leadAPI = {
  getAll: (params) => api.get('/leads', { params }),
  formConfig: () => api.get('/leads/form-config'),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  assign: (id, assignedTo, reason) => api.post(`/leads/${id}/assign`, null, { params: { assigned_to: assignedTo, ...(reason ? { reason } : {}) } }),
  // Recycle Bin
  getRecycleBin: () => api.get('/leads/recycle-bin'),
  restore: (id) => api.post(`/leads/${id}/restore`),
  permanentDelete: (id) => api.delete(`/leads/${id}/permanent`),
  getPaginated: (params) => api.get('/leads/paginated', { params }),
  kpis: (params) => api.get('/leads/kpis', { params }),
  workflowStages: () => api.get('/workflow-stages'),
  findDuplicates: (params) => api.get('/leads/duplicates', { params }),
  getTags: () => api.get('/lead-tags'),
  bulkAction: (data) => api.post('/leads/bulk', data),
  bulkExport: (leadIds) => api.post('/leads/bulk', { lead_ids: leadIds, action: 'export' }, { responseType: 'blob' }),
  getAttachments: (id) => api.get(`/leads/${id}/attachments`),
  uploadAttachment: (id, file) => {
    const body = new FormData();
    body.append('file', file);
    return api.post(`/leads/${id}/attachments`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  attachmentDownloadUrl: (leadId, attachmentId) => `${API_URL}/api/leads/${leadId}/attachments/${attachmentId}/download`
};

// Dynamic Lead Statuses
export const leadStatusAPI = {
  list: () => api.get('/lead-statuses'),
  create: (data) => api.post('/lead-statuses', data),
  update: (id, data) => api.put(`/lead-statuses/${id}`, data),
  delete: (id) => api.delete(`/lead-statuses/${id}`),
  reorder: (order) => api.post('/lead-statuses/reorder', order)
};

// Sales Targets
export const targetAPI = {
  list: (userId, includeInactive = true) => api.get('/targets', {
    params: { ...(userId ? { user_id: userId } : {}), include_inactive: includeInactive }
  }),
  create: (data) => api.post('/targets', data),
  update: (id, data) => api.put(`/targets/${id}`, data),
  delete: (id) => api.delete(`/targets/${id}`),
  progress: (id) => api.get(`/targets/${id}/progress`)
};

// Employee Performance
export const performanceAPI = {
  get: (userId, params) => api.get(`/employee-performance/${userId}`, { params })
};

// Architect
export const architectAPI = {
  projects: (mine) => api.get('/architect/projects', { params: mine ? { mine: true } : {} }),
  project: (id) => api.get(`/architect/projects/${id}`),
  updateTaskStatus: (leadId, taskStatus) => api.post(`/architect/task-status/${leadId}?task_status=${taskStatus}`),
  createTask: (id, data) => api.post(`/architect/projects/${id}/tasks`, data),
  updateTask: (id, taskId, data) => api.put(`/architect/projects/${id}/tasks/${taskId}`, data),
  uploadDrawing: (id, revision, file) => { const data = new FormData(); data.append('file', file); return api.post(`/architect/projects/${id}/drawings?revision=${revision}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  drawingUrl: (id, drawingId) => `${API_URL}/api/architect/projects/${id}/drawings/${drawingId}/download`,
  notifications: () => api.get('/architect/notifications'),
  // Group A
  getBrief: (id) => api.get(`/architect/projects/${id}/brief`),
  saveBrief: (id, data) => api.put(`/architect/projects/${id}/brief`, data),
  listDocuments: (id) => api.get(`/architect/projects/${id}/documents`),
  addDocument: (id, data) => api.post(`/architect/projects/${id}/documents`, data),
  updateDocument: (id, docId, data) => api.put(`/architect/projects/${id}/documents/${docId}`, data),
  getSiteSurvey: (id) => api.get(`/architect/projects/${id}/site-survey`),
  saveSiteSurvey: (id, data) => api.put(`/architect/projects/${id}/site-survey`, data),
  // Group B
  listDeliverables: (id) => api.get(`/architect/projects/${id}/deliverables`),
  createDeliverable: (id, data) => api.post(`/architect/projects/${id}/deliverables`, data),
  updateDeliverable: (id, delId, data) => api.put(`/architect/projects/${id}/deliverables/${delId}`, data),
  generateDeliverables: (id, data) => api.post(`/architect/projects/${id}/deliverables/generate`, data),
  deliverableTemplates: () => api.get('/architect/deliverable-templates'),
  projectOverview: (id) => api.get(`/architect/projects/${id}/overview`),
  listRevisions: (id, delId) => api.get(`/architect/projects/${id}/deliverables/${delId}/revisions`),
  addRevision: (id, delId, data) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions`, data),
  uploadRevision: (id, delId, formData) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  revisionDownloadUrl: (id, delId, revId) => `${API_URL}/api/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/download`,
  approveRevision: (id, delId, revId, data) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/approve`, data),
  listRevisionRequests: (id) => api.get(`/architect/projects/${id}/revision-requests`),
  // Foundation hardening (Batch 1)
  serviceScope: (id) => api.get(`/architect/projects/${id}/service-scope`),
  dashboard: (mine) => api.get('/architect/dashboard', { params: mine ? { mine: true } : {} }),
  dashboardList: (card, architectId, mine) => api.get('/architect/dashboard/list', { params: { card, architect_id: architectId, ...(mine ? { mine: true } : {}) } }),
  progress: (id) => api.get(`/architect/projects/${id}/progress`),
  uploadDocumentFile: (id, docId, formData) => api.post(`/architect/projects/${id}/documents/${docId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  documentFileUrl: (id, docId, fileId) => `${API_URL}/api/architect/projects/${id}/documents/${docId}/files/${fileId}/download`,
  archiveDeliverable: (id, delId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/archive`, body),
  restoreDeliverable: (id, delId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/restore`, body),
  archiveDocument: (id, docId, body) => api.post(`/architect/projects/${id}/documents/${docId}/archive`, body),
  restoreDocument: (id, docId, body) => api.post(`/architect/projects/${id}/documents/${docId}/restore`, body),
  projectProgress: (id) => api.get(`/architect/projects/${id}/progress`),
  listArchived: (id) => api.get(`/architect/projects/${id}/archived`),
  projectTimeline: (id) => api.get(`/architect/projects/${id}/timeline`),
  deliverableWorkflow: (id, delId) => api.get(`/architect/projects/${id}/deliverables/${delId}/workflow`),
  submitForReview: (id, delId, revId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/submit-review`, body || {}),
  performReview: (id, delId, revId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/review`, body),
  requestClientApproval: (id, delId, revId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/client-request`, body || {}),
  recordClientDecision: (id, delId, revId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/client-decision`, body),
  releaseIfc: (id, delId, revId, body) => api.post(`/architect/projects/${id}/deliverables/${delId}/revisions/${revId}/release`, body || {}),
  completion: (id) => api.get(`/architect/projects/${id}/completion`),
  finalSet: (id) => api.get(`/architect/projects/${id}/final-set`),
  handoverChecklist: (id) => api.get(`/architect/projects/${id}/handover/checklist`),
  generateChecklist: (id, body) => api.post(`/architect/projects/${id}/handover/checklist`, body || {}),
  updateChecklistItem: (id, itemId, body) => api.put(`/architect/projects/${id}/handover/checklist/${itemId}`, body),
  generatePackage: (id, body) => api.post(`/architect/projects/${id}/handover/package`, body || {}),
  handoverPackages: (id) => api.get(`/architect/projects/${id}/handover/packages`),
  packageDownloadUrl: (id, pkgId) => `${API_URL}/api/architect/projects/${id}/handover/packages/${pkgId}/download`,
  markReadyForHandover: (id, body) => api.post(`/architect/projects/${id}/handover/ready`, body || {}),
  recordHandover: (id, body) => api.post(`/architect/projects/${id}/handover/record`, body),
  closeProject: (id, body) => api.post(`/architect/projects/${id}/close`, body),
  reopenProject: (id, body) => api.post(`/architect/projects/${id}/reopen`, body),
  archiveDocument: (id, docId, body) => api.post(`/architect/projects/${id}/documents/${docId}/archive`, body),
  archiveProject: (id, body) => api.post(`/architect/projects/${id}/archive`, body),
  archivedItems: (id) => api.get(`/architect/projects/${id}/archived`),
  team: () => api.get('/architect/team'),
  assignmentHistory: (id) => api.get(`/architect/projects/${id}/assignment-history`),
  createRevisionRequest: (id, data) => api.post(`/architect/projects/${id}/revision-requests`, data),
  updateRevisionRequest: (id, reqId, data) => api.put(`/architect/projects/${id}/revision-requests/${reqId}`, data)
};

// Workflow Engine (Service-based Dynamic Workflows)
export const workflowAPI = {
  listStages: (serviceId) => api.get('/workflow-stages', { params: serviceId ? { service_id: serviceId } : {} }),
  createStage: (data) => api.post('/workflow-stages', data),
  updateStage: (id, data) => api.put(`/workflow-stages/${id}`, data),
  deleteStage: (id) => api.delete(`/workflow-stages/${id}`),
  reorderStages: (order) => api.post('/workflow-stages/reorder', order),
  move: (leadId, stageId, comment) => api.post(`/leads/${leadId}/workflow-move`, null, { params: { stage_id: stageId, comment } }),
  history: (leadId) => api.get(`/leads/${leadId}/workflow-history`),
  byService: () => api.get('/workflows/by-service')
};

// Follow-up APIs
export const followUpAPI = {
  getAll: (params) => api.get('/follow-ups', { params }),
  create: (data) => api.post('/follow-ups', data),
  update: (id, data) => api.put(`/follow-ups/${id}`, data),
  callCompleted: (id, notes) => api.post(`/follow-ups/${id}/call-completed`, null, { params: notes ? { notes } : {} })
};

// Note APIs
export const noteAPI = {
  getByLeadId: (leadId) => api.get(`/notes/${leadId}`),
  create: (data) => api.post('/notes', data)
};

// Activity APIs
export const activityAPI = {
  getByLeadId: (leadId) => api.get(`/activities/${leadId}`)
};

// Communication APIs
export const communicationAPI = {
  createCall: (data) => api.post('/calls', data),
  getCallsByLeadId: (leadId) => api.get(`/calls/${leadId}`),
  sendWhatsApp: (data) => api.post('/whatsapp', data),
  getWhatsAppByLeadId: (leadId) => api.get(`/whatsapp/${leadId}`),
  sendEmail: (data) => api.post('/emails', data),
  getEmailsByLeadId: (leadId) => api.get(`/emails/${leadId}`)
};

// Batch 5: Telephony & Call Intelligence
export const recordingAPI = {
  list: (params) => api.get('/calls/recordings/list', { params }),
  meta: (callId) => api.get(`/calls/${callId}/recording`),
  playbackToken: (callId) => api.post(`/calls/${callId}/recording/playback-token`),
  remove: (callId) => api.delete(`/calls/${callId}/recording`),
};

export const callAPI = {
  createCall: (data) => api.post('/calls', data),
  getByLead: (leadId) => api.get(`/calls/${leadId}`),
  disposition: (callId, data) => api.put(`/calls/${callId}/disposition`, data),
  performance: (params) => api.get('/call-performance', { params }),
  intelligence: (params) => api.get('/call-intelligence', { params }),
  ciAccess: () => api.get('/call-intelligence/access'),
  speedToLead: (params) => api.get('/call-intelligence/speed-to-lead', { params }),
  queues: () => api.get('/call-queues'),
  devices: () => api.get('/telephony/devices'),
  registerDevice: (data) => api.post('/telephony/devices', data),
  revokeDevice: (id) => api.delete(`/telephony/devices/${id}`)
};

// Calling Operations (admin controls, click-to-call, unmatched queue, devices, callbacks)
export const callingAPI = {
  getControls: (uid) => api.get(`/users/${uid}/calling-controls`),
  setControls: (uid, data) => api.put(`/users/${uid}/calling-controls`, data),
  getWorkPolicy: (uid) => api.get(`/calling/work-policy/${uid}`),
  setWorkPolicy: (uid, data) => api.put(`/calling/work-policy/${uid}`, data),
  clickToCall: (data) => api.post('/click-to-call', data),
  c2cCallers: () => api.get('/click-to-call/callers'),
  getCommand: (id) => api.get(`/telephony/commands/${id}`),
  unmatched: (params) => api.get('/unmatched-calls', { params }),
  linkLead: (callId, leadId) => api.post(`/calls/${callId}/link-lead`, { lead_id: leadId }),
  missedCallbacks: (params) => api.get('/missed-callbacks', { params }),
  callbackDone: (callId) => api.post(`/missed-callbacks/${callId}/done`),
  myDevices: (includeRevoked = false) => api.get('/telephony/devices/mine', { params: { include_revoked: includeRevoked } }),
  setDefaultDevice: (id) => api.post(`/telephony/devices/${id}/default`),
  renameDevice: (id, name) => api.post(`/telephony/devices/${id}/rename`, { device_name: name }),
  removeDevice: (id) => api.post(`/telephony/devices/${id}/remove`),
  deleteDevicePermanent: (id) => api.delete(`/telephony/devices/${id}/permanent`),
  dedupeDevices: (userId) => api.post(`/telephony/devices/dedupe/${userId}`),
  pairStart: (data) => api.post('/telephony/devices/pair/start', data || {}),
  pairStatus: (code) => api.get(`/telephony/devices/pair/${code}/status`),
  // Calling connection health (monitoring only; reuses existing C2C telemetry)
  healthSummary: () => api.get('/telephony/health/summary'),
  healthDevices: (params = {}) => api.get('/telephony/health/devices', { params }),
  healthDevice: (id) => api.get(`/telephony/health/devices/${id}`),
  connectionTests: (deviceId, limit = 20) => api.get('/telephony/connection-tests', { params: { device_id: deviceId, limit } }),
  testConnection: (deviceId) => api.post(`/telephony/devices/${deviceId}/test-connection`, {}, { timeout: 60000 })
};

// Per-user lead workflow controls + self-lead approval
export const leadControlsAPI = {
  get: (uid) => api.get(`/users/${uid}/lead-controls`),
  set: (uid, data) => api.put(`/users/${uid}/lead-controls`, data)
};
export const leadApprovalAPI = {
  pending: () => api.get('/leads-pending-approval'),
  approve: (id) => api.post(`/leads/${id}/approve`),
  reject: (id, reason) => api.post(`/leads/${id}/reject`, { reason }),
  requestChanges: (id, reason) => api.post(`/leads/${id}/request-changes`, { reason })
};

// Settings APIs
export const settingsAPI = {
  getLeadSources: () => api.get('/lead-sources'),
  getServices: (includeInactive = false) => api.get('/services', { params: includeInactive ? { include_inactive: true } : {} }),
  getCompany: () => api.get('/settings/company'),
  updateCompany: (data) => api.put('/settings/company', data),
  getLeakSla: () => api.get('/settings/leak-sla'),
  updateLeakSla: (values) => api.put('/settings/leak-sla', { values }),
  resetLeakSla: () => api.post('/settings/leak-sla/reset')
};

// Service Management APIs (Super Admin)
export const serviceAPI = {
  archive: (id, body) => api.post(`/services/${id}/archive`, body || {}),
  getAll: (includeInactive = true) => api.get('/services', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  toggleActive: (id) => api.post(`/services/${id}/toggle-active`)
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getPipeline: () => api.get('/dashboard/pipeline'),
  getServiceStats: () => api.get('/dashboard/service-stats'),
  // Single aggregated read that powers the whole Sales Executive dashboard.
  // userId is allowed only for a Super Admin or the executive's mapped manager (read context).
  getSalesExecutive: (period, userId) => api.get('/dashboard/sales-executive', {
    params: { period, ...(userId ? { user_id: userId } : {}) }
  }),
  // Single aggregated read for the Sales Manager dashboard (own work + mapped team).
  getSalesManager: (period, userId) => api.get('/dashboard/sales-manager', {
    params: { period, ...(userId ? { user_id: userId } : {}) }
  })
};

// Reports APIs
export const reportsAPI = {
  getLeadsReport: (params) => api.get('/reports/leads', { params }),
  exportCSV: (params) => api.get('/reports/leads', {
    params: { ...params, export_format: 'csv' },
    responseType: 'blob'
  })
};

// Follow-up snooze
export const followUpSnooze = (id, minutes) => api.post(`/follow-ups/${id}/snooze`, null, { params: { minutes } });

// Quotations
export const quotationAPI = {
  getByLead: (leadId) => api.get(`/quotations/lead/${leadId}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`)
};

// DPR & Sales Report
export const dprAPI = {
  get: (params) => api.get('/dpr', { params }),
  getTeamUsers: () => api.get('/dpr/team'),
  export: (params) => api.get('/dpr/export', {
    params,
    responseType: 'blob'
  })
};

export const salesReportAPI = {
  get: (params) => api.get('/sales-report', { params }),
  export: (params) => api.get('/sales-report/export', {
    params,
    responseType: 'blob'
  })
};

// Dashboard drill-down detail pages
export const dashboardDetailsAPI = {
  newLeadsToday: () => api.get('/dashboard/details/new-leads-today'),
  hotLeads: () => api.get('/dashboard/details/hot-leads'),
  followupsToday: () => api.get('/dashboard/details/followups-today'),
  missedFollowups: () => api.get('/dashboard/details/missed-followups'),
  revenue: () => api.get('/dashboard/details/revenue'),
  delayedProjects: (thresholdDays) => api.get('/dashboard/delayed-projects', { params: thresholdDays ? { threshold_days: thresholdDays } : {} }),
  reassignSuggestions: (leadId) => api.get(`/dashboard/reassign-suggestions/${leadId}`)
};

// AI APIs
export const aiAPI = {
  calculateLeadScore: (leadId) => api.post(`/ai/lead-score/${leadId}`),
  getFollowUpSuggestions: (leadId) => api.post(`/ai/follow-up-suggestions/${leadId}`)
};

// ==================== Batch 3 APIs ====================
export const analyticsAPI = {
  leadPerformance: (params) => api.get('/analytics/lead-performance', { params }),
  adminDashboard: (params) => api.get('/analytics/admin-dashboard', { params })
};

export const scopeAPI = {
  list: (leadId) => api.get(`/leads/${leadId}/scope-items`),
  create: (leadId, data) => api.post(`/leads/${leadId}/scope-items`, data),
  submit: (leadId, itemId) => api.post(`/leads/${leadId}/scope-items/${itemId}/submit`),
  approve: (leadId, itemId) => api.post(`/leads/${leadId}/scope-items/${itemId}/approve`),
  reject: (leadId, itemId, reason) => api.post(`/leads/${leadId}/scope-items/${itemId}/reject`, { reason }),
  modify: (leadId, itemId, data) => api.patch(`/leads/${leadId}/scope-items/${itemId}`, data),
  setStatus: (leadId, itemId, status, reason) => api.post(`/leads/${leadId}/scope-items/${itemId}/status`, { status, reason }),
  cancel: (leadId, itemId, reason) => api.post(`/leads/${leadId}/scope-items/${itemId}/cancel`, { reason }),
  financialAdjust: (leadId, itemId, delta, reason) => api.post(`/leads/${leadId}/scope-items/${itemId}/financial-adjust`, { delta, reason })
};

export const financeAPI = {
  categories: () => api.get('/finance/expense-categories'),
  createCategory: (name) => api.post('/finance/expense-categories', { name }),
  deleteCategory: (id) => api.delete(`/finance/expense-categories/${id}`),
  expenses: (params) => api.get('/finance/expenses', { params }),
  createExpense: (data) => api.post('/finance/expenses', data),
  updateExpense: (id, data) => api.put(`/finance/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/finance/expenses/${id}`),
  profitability: (params) => api.get('/finance/profitability', { params }),
  aggregate: () => api.get('/finance/aggregate')
};

export const importAPI = {
  parse: (formData) => api.post('/import/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  gsheetFetch: (payload) => api.post('/import/gsheet/fetch',
    typeof payload === 'string' ? { url: payload } : payload),
  sheetSources: () => api.get('/import/gsheet/sources'),
  sheetSourcesAll: () => api.get('/import/gsheet/sources/all'),
  createSheetSource: (data) => api.post('/import/gsheet/sources', data),
  updateSheetSource: (id, data) => api.put(`/import/gsheet/sources/${id}`, data),
  testSheetSource: (id) => api.post(`/import/gsheet/sources/${id}/test`),
  serviceAccount: () => api.get('/import/gsheet/service-account'),
  preview: (data) => api.post('/import/preview', data),
  confirm: (data) => api.post('/import/confirm', data),
  batches: (params) => api.get('/import/batches', { params }),
  batchLeads: (batchId) => api.get(`/import/batches/${batchId}/leads`),
  rollback: (batchId) => api.delete(`/import/batches/${batchId}`)
};

export const notificationPrefsAPI = {
  get: () => api.get('/notifications/preferences'),
  set: (preferences) => api.put('/notifications/preferences', { preferences }),
  deliveries: () => api.get('/notifications/deliveries'),
  dispatch: () => api.post('/notifications/dispatch')
};

export const exportAPI = {
  download: (report, params) => api.get(`/export/${report}`, { params, responseType: 'blob' })
};

export const leadDetail360API = {
  get: (leadId) => api.get(`/leads/${leadId}/detail360`),
  workflow: (leadId) => api.get(`/leads/${leadId}/workflow-summary`)
};

export const receiptAPI = {
  generate: (leadId, data) => api.post(`/leads/${leadId}/receipt`, data),
  list: (leadId) => api.get(`/leads/${leadId}/receipts`)
};

// Helper: trigger a browser download from a blob response
export const downloadBlob = (response, filename) => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default api;
